'use client';

import { c as _c } from "react/compiler-runtime";
import { AssetPath } from '@gnsx/genesys.js';
import { AlertTriangleIcon, CloudUploadIcon, PlusIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useRef } from 'react';
import { GamePreview } from './GamePreview';
import type { GameRecordDirectory } from '@/lib/games';
import type { IStorageProvider } from '@/lib/storage';
import { CreateGameDialog } from '@/app/dashboard/components/CreateGameDialog';
import { GameAssetUploader } from '@/app/dashboard/components/GameAssetUploader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { store } from '@/editor/store';
import { GamesProvider } from '@/lib/games';
import { storageFactory } from '@/lib/storage';
import { GameQueryParamsValues } from '@/types/game';
interface UploadProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
type ProjectState = {
  projectData: any;
  projectFile: string;
  gameId: string;
};
function UploadDialogContent({
  open,
  onOpenChange
}: UploadProjectDialogProps) {
  const hostingProvider = useRef<IStorageProvider>(storageFactory.getDefaultProvider());
  const [isLoadingProjectData, setIsLoadingProjectData] = useState(false);
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [games, setGames] = useState<GameRecordDirectory[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [createGameError, setCreateGameError] = useState<string | null>(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [projectState, setProjectState] = useState<ProjectState | null | undefined>(undefined);
  async function loadGames() {
    setIsLoadingGames(true);
    try {
      const games = await GamesProvider.listGames(hostingProvider.current, {
        onlyMyGames: true,
        published: GameQueryParamsValues.All
      });
      setGames(games);
      console.log(games);
    } finally {
      setIsLoadingGames(false);
    }
  }
  const loadProjectData = useCallback(async () => {
    setIsLoadingProjectData(true);
    try {
      const projectProvider = store.project.makeScopedStorageProvider();
      const projectFile = store.project.projectFilePathWithProjectPrefix;
      if (projectFile) {
        const projectData = await projectProvider.downloadFileAsJson(AssetPath.fromString(projectFile));
        const projectState = {
          projectData,
          projectFile: projectFile,
          gameId: (projectData as any).gameId ?? ''
        };
        setProjectState(projectState);
        console.log(projectState);
        loadGames();
      } else {
        console.error('No project file found in storage');
        setProjectState(null);
        setGames([]);
      }
    } finally {
      setIsLoadingProjectData(false);
    }
  }, []);
  const updateProjectGameId = async (gameId: string) => {
    if (!projectState) return;
    setIsUpdatingProject(true);
    try {
      const projectProvider = store.project.makeScopedStorageProvider();
      const updatedProjectData = {
        ...projectState.projectData,
        gameId: gameId
      };
      await projectProvider.uploadFile(AssetPath.fromString(projectState.projectFile), JSON.stringify(updatedProjectData, null, 2), {
        contentType: 'application/json'
      });

      // Update local state
      setProjectState({
        ...projectState,
        gameId: gameId,
        projectData: updatedProjectData
      });
      setSelectedGameId(null);
      console.log('Project updated with gameId:', gameId);
    } catch (error) {
      console.error('Failed to update project:', error);
    } finally {
      setIsUpdatingProject(false);
    }
  };
  const handleGameSelect = (game: GameRecordDirectory) => {
    setSelectedGameId(game.gameId);
  };
  const handleConfirmSelection = () => {
    if (selectedGameId) {
      updateProjectGameId(selectedGameId);
    }
  };
  const handleGameCreated = () => {
    // Reload games after creating a new one
    loadGames();
    setCreateGameError(null);
  };
  const handleCreateGameError = (error_0: string) => {
    setCreateGameError(error_0);
  };
  useEffect(() => {
    if (open) {
      if (projectState === undefined) {
        loadProjectData();
      }
    } else {
      setProjectState(undefined);
      setGames([]);
      setSelectedGameId(null);
      setCreateGameError(null);
      setIsUploadDialogOpen(false);
    }
  }, [open, projectState, loadProjectData]);
  if (isLoadingProjectData) {
    return <div className="flex items-center justify-center h-32 border-2 border-dashed border-border rounded-lg">
        <p className="text-muted-foreground">Loading project data...</p>
      </div>;
  }
  if (projectState === null) {
    return <div className="flex items-center justify-center h-32 border-2 border-dashed border-yellow-300 bg-yellow-50 rounded-lg">
        <div className="text-center">
          <AlertTriangleIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
          <p className="text-yellow-800 font-medium">No project data found</p>
          <p className="text-yellow-700 text-sm">No project file was found in the storage.</p>
        </div>
      </div>;
  }
  if (isLoadingGames) {
    return <div className="flex items-center justify-center h-32 border-2 border-dashed border-border rounded-lg">
        <p className="text-muted-foreground">Loading games...</p>
      </div>;
  }

  // If gameId is set and game is found, show the linked game
  if (projectState && projectState.gameId) {
    const targetGame = games.find(game_0 => game_0.gameId === projectState.gameId);
    if (targetGame) {
      return <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Project is linked to the following game in the cloud:
            </p>
          </div>
          <div className="flex justify-center">
            <GamePreview game={targetGame} />
          </div>
          <div className="flex justify-center pt-4 border-t">
            <Button onClick={() => setIsUploadDialogOpen(true)} className="w-full max-w-xs flex items-center gap-2">
              <CloudUploadIcon className="h-4 w-4" />
              Upload
            </Button>
          </div>

          {/* Asset Uploader Dialog */}
          <GameAssetUploader gameId={targetGame.gameId} isOpen={isUploadDialogOpen} onClose={() => setIsUploadDialogOpen(false)} sourceProvider={store.project.makeScopedStorageProvider()} onUploadComplete={() => {
          void loadGames();
          onOpenChange(false);
        }} />
        </div>;
    }
  }

  // If gameId is not set or game is not found, show all games for selection
  // Always show create game option when no game is linked
  return <div className="space-y-4">
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-4">
          {projectState?.gameId ? 'Game not found. Please select a game to link to this project:' : games.length === 0 ? 'No games found. Create a game to link to this project:' : 'Select a game to link to this project:'}
        </p>
      </div>

      {games.length === 0 ?
    // When no games exist, show only the create button prominently
    <div className="flex justify-center">
          <CreateGameDialog loading={isLoadingGames} onSuccess={handleGameCreated} onError={handleCreateGameError} className="w-full max-w-xs" />
        </div> :
    // When games exist, show them in a grid with create option
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-96 overflow-y-scroll p-2">
          {games.map(game_1 => <div key={game_1.gameId} className={`cursor-pointer transition-all duration-200 rounded-lg m-1 ${selectedGameId === game_1.gameId ? 'ring-2 ring-primary ring-offset-2 scale-105' : 'hover:scale-102 hover:shadow-md'}`} onClick={() => handleGameSelect(game_1)}>
              <GamePreview game={game_1} />
            </div>)}

          {/* Create New Game Card - Always shown when no game is linked */}
          <div className="m-1">
            <div className="w-full max-w-xs">
              <div className="border-2 border-dashed border-primary/30 rounded-lg hover:border-primary/50 transition-colors bg-primary/5 hover:bg-primary/10 aspect-[4/3] flex flex-col items-center justify-center text-center p-3">
                <PlusIcon className="h-8 w-8 text-primary mb-2" />
                <CreateGameDialog loading={isLoadingGames} onSuccess={handleGameCreated} onError={handleCreateGameError} className="text-xs px-2 py-1 h-auto bg-transparent hover:bg-primary/20 border-none shadow-none" />
              </div>
            </div>
          </div>
        </div>}

      {createGameError && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-center">
          {createGameError}
        </div>}

      {selectedGameId && <div className="flex justify-center pt-4 border-t">
          <Button onClick={handleConfirmSelection} disabled={isUpdatingProject} className="w-full max-w-xs">
            {isUpdatingProject ? 'Linking Game...' : 'Link Selected Game'}
          </Button>
        </div>}
    </div>;
}
export const UploadProjectDialog = t0 => {
  const $ = _c(13);
  const {
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogHeader><DialogTitle className="flex items-center gap-2"><CloudUploadIcon className="size-4" />Upload Project</DialogTitle><DialogDescription>Link your project to a game to enable uploading.</DialogDescription></DialogHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onOpenChange || $[2] !== open) {
    t2 = <div className="flex-1 overflow-y-scroll"><UploadDialogContent open={open} onOpenChange={onOpenChange} /></div>;
    $[1] = onOpenChange;
    $[2] = open;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onOpenChange) {
    t3 = <DialogFooter><Button onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>;
    $[4] = onOpenChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">{t1}{t2}{t3}</DialogContent>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onOpenChange || $[10] !== open || $[11] !== t4) {
    t5 = <Dialog open={open} onOpenChange={onOpenChange}>{t4}</Dialog>;
    $[9] = onOpenChange;
    $[10] = open;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};